import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { ROUTES } from 'theSource/app/routes';
import accessDenied from 'theSource/assets/accessDenied.png';
import {
  AccessDeniedContainerStyled,
  AccessDeniedImageStyled,
  AdminLoginNoticeTypographyStyled,
  ButtonContainerStyled,
  InfoNoticeTypographyStyled,
  LogoContainerStyled,
} from './components/AccessDeniedStyled';
import { Button, Loader, Spacer } from 'theSource/components';
import { setIsAutomaticAuthDisabled } from 'theSource/services';
import { Logo } from 'theSource/components/Logo';
import { useApiMutation } from 'theSource/utils';
import { LOGOFF_QUERY_KEY } from 'theSource/components/Header/components/utils';
import { setAuthenticated } from 'theSource/features/Login';
import { resetTermsOfUseAcceptance } from 'theSource/features/TermsOfUse';
import {
  CompanyManifestResponseDto,
  GET_COMPANY_MANIFEST_QUERY_KEY,
  useAdminRedirectQuery,
  useConfigurationQuery,
  useCompanySettingsQuery,
} from 'theSource/queries';
import { setIsAdmin } from 'theSource/features/AccessDenied';

export const AccessDenied = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation('pages.accessDenied');
  const [isLoadingRedirect, setIsLoadingRedirect] = useState(false);

  dispatch(setAuthenticated(false));
  dispatch(setIsAdmin(true));

  const { admin, isLoadingAdmin } = useAdminRedirectQuery();
  const manifestData: CompanyManifestResponseDto = queryClient.getQueryData(
    GET_COMPANY_MANIFEST_QUERY_KEY
  )!;
  const { configuration, isLoadingConfiguration } = useConfigurationQuery();

  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  const { mutate: logoff } = useApiMutation<{}, {}, {}>({
    queryKey: LOGOFF_QUERY_KEY,
    method: 'put',
    endpoint: '/logoff',
    onSuccess: () => {
      navigate(ROUTES.main.home.path);
      dispatch(setAuthenticated(false));
      dispatch(resetTermsOfUseAcceptance());
      dispatch(setIsAutomaticAuthDisabled(true));
    },
    showSuccessSnackbar: false,
    snackbarOptions: {
      position: 'bottom',
    },
  });

  const handleLogoff = () => logoff({});
  const handleRedirect = () => {
    // added a .2sec delay to enhance feel, instant reload to loader is odd
    setTimeout(() => {
      setIsLoadingRedirect(true);
      if (admin?.data && admin?.data !== '') {
        if (String(admin?.data).startsWith('http')) {
          window.location.assign(String(admin?.data));
        } else {
          window.location.assign(configuration?.apiHost + String(admin?.data));
        }
      } else {
        window.location.assign(configuration?.apiHost || '/');
      }
    }, 200);
  };

  const isLoading =
    isLoadingConfiguration || isLoadingAdmin || isLoadingRedirect;

  if (isLoading) {
    return <Loader />;
  }

  if (
    !isLoading &&
    companySettings?.data.lumos_feature_toggle &&
    !companySettings?.data.source_license
  ) {
    navigate(ROUTES.enrollment.path);
  }

  return (
    <AccessDeniedContainerStyled data-testid="access-denied">
      <LogoContainerStyled>
        <Logo />
      </LogoContainerStyled>
      <AccessDeniedImageStyled src={accessDenied} />
      <AdminLoginNoticeTypographyStyled variant="h5">
        {t('AdminNotice')}
      </AdminLoginNoticeTypographyStyled>
      <InfoNoticeTypographyStyled variant="subtitle1">
        {t('InfoNotice', { app_name: manifestData!.name })}
      </InfoNoticeTypographyStyled>
      <ButtonContainerStyled>
        <Button
          data-testid="admin-button"
          label={t('DashButton')}
          variant="primary"
          fullWidth={false}
          onClick={handleRedirect}
        />
        <Spacer height={6} />
        <Button
          isLoading={isLoading}
          data-testid="logout-button"
          label={t('common:logout')}
          variant="tertiary"
          fullWidth={false}
          onClick={handleLogoff}
        />
      </ButtonContainerStyled>
    </AccessDeniedContainerStyled>
  );
};
